import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartSyncedEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent, CartCustomAddEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * Normalizes line items returned by Shopify /cart/add.js.
 * @param {Record<string, unknown>} response
 * @returns {Record<string, unknown>[]}
 */
function getCartAddResponseItems(response) {
  if (Array.isArray(response.items)) return response.items;
  if (response.variant_id != null || response.id != null) return [response];
  return [];
}

/**
 * Merges requested line items with matching items from the add-to-cart API response.
 * @param {Record<string, unknown>[]} requestedLineItems
 * @param {Record<string, unknown>[]} responseItems
 * @returns {Record<string, unknown>[]}
 */
function buildAddedItemDetails(requestedLineItems, responseItems) {
  return requestedLineItems.map((requestedItem) => {
    const variantId = requestedItem.id;
    const matched = responseItems.find(
      (item) => String(item.variant_id ?? item.id) === String(variantId)
    );
    return matched ? { ...requestedItem, ...matched } : { ...requestedItem };
  });
}

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */(timeoutId) => clearTimeout(timeoutId));
    }
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  // @ts-ignore
  // @ts-ignore
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      this.animateAddToCart();
    }
  }


  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */(timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement | undefined} liveRegion - The live region (omit when using `data-live-region-id` on the host).
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 * @property {HTMLElement | undefined} secureShieldComponent - The secure shield component.
 * @property {HTMLElement | undefined} purchaseInBulkComponent - The purchase in bulk component.
 * @property {HTMLElement | undefined} buyMoreSaveMoreComponent - The buy more save more component.
 * @property {HTMLElement | undefined} buyTheBundleComponent - The buy the bundle component.
 * @property {HTMLElement | undefined} completeYourLookComponent - The complete your look component.
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  /** When `data-live-region-id` is set, announcements use that element instead of `ref="liveRegion"` (avoids duplicate hidden live regions per card). */
  // @ts-ignore
  get requiredRefs() {
    return this.dataset.liveRegionId ? ['variantId'] : ['variantId', 'liveRegion'];
  }

  renderedRefs = ['breadcrumb-component', 'star-rating-component', 'estimated-shipping-component', 'budget-pay-component', 'secure-shield-component', 'purchase-in-bulk-component', 'buy-the-bundle-component', 'complete-your-look-component', 'product-accordion-component', 'quantity-selector-component', 'product-inventory-component', 'product-title-default__container', 'product-title-default', 'price-comparison-block']; /** @type {Array<keyof ProductFormRefs>} */
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    this.productDetailContainer = this.closest("[data-id='product-information']");
    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    // @ts-ignore
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component' || event.detail?.source === 'budget-pay') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);
    const plainObject = Object.fromEntries(formData.entries());

    /** @type {Record<string, string>} */
    const lineItemProperties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (!match || typeof value !== 'string') continue;

      // Ignore empty values so we only send fields selected by the customer.
      if (!value.trim()) continue;
      // @ts-ignore
      lineItemProperties[match[1]] = value;
    }

    

    const mainLineItem = {
      // @ts-ignore
      id: parseInt(plainObject.id),
      // @ts-ignore
      quantity: parseInt(plainObject.quantity) || Number(this.dataset.quantityDefault) || 1,
    };

    if (Object.keys(lineItemProperties).length > 0) {
      // @ts-ignore
      mainLineItem.properties = lineItemProperties;
    }

    // Add Loop plan if widget is selected 
    const loopPlanWidget = this.productDetailContainer?.querySelector('.loop-widget-container');
    if (loopPlanWidget && !loopPlanWidget.querySelector('.loop-w-btn-group-description-container')?.classList.contains('loop-display-none')) {
      // @ts-ignore
      const sellingPlan = loopPlanWidget.querySelector("select[name='selling_plan']")?.value || document.querySelector("select[name='selling_plan']")?.value;

      if (sellingPlan) {
        // @ts-ignore
        mainLineItem.selling_plan = parseInt(sellingPlan);
        // @ts-ignore
        mainLineItem.properties = {
          "_product_type": "subscription"
        }
      }
    }

    const items = {
      items: [mainLineItem],
    };

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      // @ts-ignore
      items['sections'] = cartItemComponentsSectionIds.join(',');
    });

    // const secure_shields = this.productDetailContainer?.querySelectorAll('.secure-shield-block')
    // const is_secureshield_checked = Array.from(secure_shields).some((shield) => shield.querySelector('input[name="secure_shield_protection"]')?.checked)

    const secure_shields =
      this.productDetailContainer?.querySelectorAll('.secure-shield-block') ?? [];

    const is_secureshield_checked = [...secure_shields].some((shield) =>
      // @ts-ignore
      shield.querySelector('input[name="secure_shield_protection"]')?.checked
    );

    if (secure_shields?.length > 0 && is_secureshield_checked) {
      // @ts-ignore
      let secureShieldId = parseInt(this.productDetailContainer?.querySelector('input[name="secure_shield_protection"]')?.value);
      // @ts-ignore
      let parentID = parseInt(plainObject.id);
      // @ts-ignore
      const updatedItems = items.items.map(item => {
        // @ts-ignore
        if (item.id === parentID) {
          return {
            ...item,
            properties: {
              // @ts-ignore
              ...item.properties, // preserve existing if any
              // @ts-ignore
              _has_secureshield: true,
              _secureshield_id: secureShieldId
            }
          };
        }
        return item;
      });

      items.items = updatedItems;

      items.items.push({
        // @ts-ignore
        id: secureShieldId,
        // @ts-ignore
        parent_id: parentID,
        // @ts-ignore
        quantity: parseInt(plainObject.quantity),
        // @ts-ignore
        properties: {
          "_product_type": "secure_shield"
        }
      })
    }

    if (this.productDetailContainer?.querySelector('packaging-box') && this.productDetailContainer?.querySelector('packaging-box packaging-box-item.active')) {
      items.items.push({
        // @ts-ignore
        id: parseInt(this.productDetailContainer?.querySelector('packaging-box packaging-box-item.active')?.dataset.variantId),
        quantity: 1
      })
    }

    const fetchCfg = fetchConfig('json', { body: JSON.stringify(items) });

    fetch(Theme.routes.cart_add_url, fetchCfg)
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

          const responseItems = getCartAddResponseItems(response);

          this.dispatchEvent(
            new CartCustomAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              variantId: formData.get('id')?.toString(),
              sections: response.sections,
              addedItems: buildAddedItemDetails(items.items, responseItems),
              items: responseItems,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

          const responseItems = getCartAddResponseItems(response);

          this.dispatchEvent(
            new CartCustomAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              variantId: id.toString(),
              sections: response.sections,
              addedItems: buildAddedItemDetails(items.items, responseItems),
              items: responseItems,
            })
          );

          // Fetch the updated cart to get the actual total quantity for this variant
          await this.#fetchAndUpdateCartQuantity();
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
        document.dispatchEvent(new CartSyncedEvent(undefined, this.id, { source: 'product-form-component' }));
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @returns {HTMLElement | undefined}
   */
  #getLiveRegion() {
    const id = this.dataset.liveRegionId;
    if (id) {
      const el = document.getElementById(id);
      if (el instanceof HTMLElement) return el;
    }
    return this.refs.liveRegion;
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.#getLiveRegion();
    if (liveRegion) liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.#getLiveRegion();
    if (liveRegion) liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {

      morph(currentElement, newElement);

      // Reinitialize form handlers for custom elements after morphing
      // Use queueMicrotask to ensure morphing is complete and Component refs are updated
      queueMicrotask(() => {
        this.#reinitializeMorphedFormHandlers(currentElement);
      });
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      const insertedElement = /** @type {Element} */ (newElement.cloneNode(true));
      insertReferenceElement.insertAdjacentElement('beforebegin', insertedElement);

      // Reinitialize form handlers for newly inserted elements
      queueMicrotask(() => {
        this.#reinitializeMorphedFormHandlers(insertedElement);
      });
    }
  }

  /**
   * Reinitializes form submission handlers for custom elements after morphing
   * @param {Element} morphedElement - The element that was morphed
   */
  #reinitializeMorphedFormHandlers(morphedElement) {
    // Find all complete-your-look-product components within the morphed element
    const completeYourLookProducts = morphedElement.querySelectorAll('complete-your-look-product');
    for (const component of completeYourLookProducts) {
      const customElement = /** @type {any} */ (component);
      if (customElement.reinitializeFormSubmission && typeof customElement.reinitializeFormSubmission === 'function') {
        customElement.reinitializeFormSubmission();
      }
    }

    // Find all purchase-in-bulk-product components within the morphed element
    const purchaseInBulkProducts = morphedElement.querySelectorAll('purchase-in-bulk-product');
    for (const component of purchaseInBulkProducts) {
      const customElement = /** @type {any} */ (component);
      if (customElement.reinitializeFormSubmission && typeof customElement.reinitializeFormSubmission === 'function') {
        customElement.reinitializeFormSubmission();
      }
    }

    // Also check if the morphed element itself is one of these components
    if (morphedElement.tagName === 'COMPLETE-YOUR-LOOK-PRODUCT') {
      const customElement = /** @type {any} */ (morphedElement);
      if (customElement.reinitializeFormSubmission && typeof customElement.reinitializeFormSubmission === 'function') {
        customElement.reinitializeFormSubmission();
      }
    }
    if (morphedElement.tagName === 'PURCHASE-IN-BULK-PRODUCT') {
      const customElement = /** @type {any} */ (morphedElement);
      if (customElement.reinitializeFormSubmission && typeof customElement.reinitializeFormSubmission === 'function') {
        customElement.reinitializeFormSubmission();
      }
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';
    variantId.dataset.variantSku = event.detail.resource?.sku ?? '';
    // @ts-ignore
    variantId.dataset.variantPrice = event.detail.resource?.price ? event.detail.resource?.price/100 :  '';
    const { acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButtonElement = this.querySelector('.add-to-cart-button-component');

    const newAddToCartButtonElement = event.detail.data.html.querySelector('product-form-component .add-to-cart-button-component');
    if (newAddToCartButtonElement && currentAddToCartButtonElement) {
      morph(currentAddToCartButtonElement, newAddToCartButtonElement);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }


    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    this.#updateRenderableRefs(event.detail.data.html);

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };

  /**
   * Scopes variant-update HTML to the product information region so morph targets
   * match live DOM order (avoids unrelated `.product-title-default` nodes elsewhere on the page).
   * @param {Document | Element} html
   * @returns {Element | Document}
   */
  #getProductInformationHtml(html) {
    const scoped =
      html instanceof Document
        ? html.querySelector('[data-id="product-information"]')
        : html.querySelector?.('[data-id="product-information"]');
    return scoped ?? html;
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | Document} html - The current element in the DOM
   */
  #updateRenderableRefs(html) {
    const renderableRefs = this.renderedRefs;
    const productInformationHtml = this.#getProductInformationHtml(html);

    renderableRefs.forEach((ref) => {

      if (ref === 'breadcrumb-component') {
        const currentBreadCrumbs = document.querySelectorAll('.breadcrumb-component');
        const newBreadCrumbs = html.querySelectorAll('.breadcrumb-component');

        currentBreadCrumbs?.forEach((element, index) => {
          this.#morphOrUpdateElement(element, newBreadCrumbs[index]);
        })
      } else {
        const currentElements = this.productDetailContainer?.querySelectorAll(`.${ref}`);
        const newElements = productInformationHtml.querySelectorAll(`.${ref}`);

        currentElements?.forEach((element, index) => {
          this.#morphOrUpdateElement(element, newElements[index]);
        })
      }
    });

    // After all morphing is complete, reinitialize form handlers for all affected components
    // This ensures form handlers are reattached even if components weren't directly morphed
    queueMicrotask(() => {
      if (this.productDetailContainer) {
        // Find all complete-your-look-product components in the product detail container
        const completeYourLookProducts = this.productDetailContainer.querySelectorAll('complete-your-look-product');
        for (const component of completeYourLookProducts) {
          const customElement = /** @type {any} */ (component);
          if (customElement.reinitializeFormSubmission && typeof customElement.reinitializeFormSubmission === 'function') {
            customElement.reinitializeFormSubmission();
          }
        }

        // Find all purchase-in-bulk-product components in the product detail container
        const purchaseInBulkProducts = this.productDetailContainer.querySelectorAll('purchase-in-bulk-product');
        for (const component of purchaseInBulkProducts) {
          const customElement = /** @type {any} */ (component);
          if (customElement.reinitializeFormSubmission && typeof customElement.reinitializeFormSubmission === 'function') {
            customElement.reinitializeFormSubmission();
          }
        }
      }
    });
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}